import { Paper, Divider } from '@mui/material';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { withStyles } from 'tss-react/mui';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { FilterNone as ContainersIcon, Info as InfoIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  loadCompanies,
  loadCompaniesFilters,
  loadCompaniesPage,
  loadCompaniesRowsPerPage,
} from '../../store/actions/companiesActions';
import { forwardContainers, setRefreshContainers } from '../../store/actions/containerActions';
import { errorMessage, successMessage } from '../../store/actions/messageActions';
import { loadSelectedContainersSucceeded } from '../../store/actions/selectedContainerActions';
import { selectedStakeholderChanged } from '../../store/actions/selectedStakeholderActions';
import {
  getCompanies,
  getCompaniesLoading,
  getCompaniesPaging,
  getSelectedContainers,
} from '../../store/selectors/selectors';
import { BaseRoleCodes, LABEL } from '../../theme/constants';
import CompanyFilter from '../organisms/CompanyFilter';
import CompanyTable from '../organisms/CompanyTable';
import ContainerTable from '../organisms/ContainerTable';
import LoaderTimeout from '../organisms/LoaderTimeout';
import { hasBaseRole } from 'auth/helper';
import { useNavigate, useParams } from 'react-router-dom';

const styles = (theme) => ({
  content: {
    width: '100%',
  },
  tabBar: {
    flexWrap: 'nowrap',
    display: 'flex',
    flexFlow: 'row wrap',
  },
  tabs: {
    flex: 1,
  },
  paperSlim: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    width: '100%',
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    width: '100%',
    position: 'relative',
    // minHeight: '560px',
  },
  divider: {
    marginBottom: theme.spacing(2),
  },
  companiesCircularProgress: {
    marginLeft: '50%',
    marginTop: '5%',
  },
  breadBar: {
    marginBottom: theme.spacing(2),
  },
});

const columns = [
  { id: 'deleteForeverIcon' },
  { id: 'billOfLading', label: LABEL.BILL_OF_LADING_LABEL },
  { id: 'equipmentNumber', label: LABEL.EQUIPMENT_NUMBER_LABEL },
  { id: 'shippingAgent', label: LABEL.SHIPPIN_AGENT_LABEL },
  { id: 'receivedFrom', label: LABEL.RECEIVED_FROM_LABEL },
  { id: 'receivedOn', label: LABEL.RECEIVED_ON_LABEL },
  { id: 'validUntil', label: LABEL.VALID_UNTIL_LABEL },
  { id: 'terminal', label: LABEL.TERMINAL_LABEL },
  { id: 'status', label: LABEL.STATUS_LABEL },
];

const columnsCompanies = [
  { id: 'nxtEntityId', label: LABEL.NXTENTITY_ID_LABEL },
  { id: 'apcs-code', label: LABEL.APCS_CODE },
  { id: 'eori', label: LABEL.EORI },
  { id: 'duns', label: LABEL.DUNS },
  { id: 'name', label: LABEL.NAME_LABEL },
  { id: 'taxId', label: LABEL.TAX_ID_LABEL },
  { id: 'transfer', label: LABEL.TRANSFER },
];

const ContainerDetail = (props) => {
  const {
    items,
    paging,
    classes,
    companies,
    loadCompanies,
    loading,
    loadCompaniesPage,
    loadCompaniesRowsPerPage,
    loadSelectedContainersSucceeded,
    forwardContainers,
    setRefreshContainers,
  } = props;

  const { tabId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const getColumns = () => {
    if (hasBaseRole(BaseRoleCodes.TERMINAL_CODE)) {
      return columns.filter((column) => column.id !== 'terminal' && column.id !== 'receivedFrom');
    } else if (hasBaseRole(BaseRoleCodes.SHIP_AGENT_CODE)) {
      return columns.filter(
        (column) => column.id !== 'shippingAgent' && column.id !== 'receivedFrom'
      );
    } else {
      return columns;
    }
  };

  const handleChangeTab = (event, value) => {
    navigate(`/containers/${value}`);
  };

  return (
    <React.Fragment>
      <main className={classes.content}>
        <Divider />
        <div className={classes.tabBar}>
          <div className={classes.tabs}>
            <Tabs
              value={tabId}
              onChange={handleChangeTab}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab value="company" label={LABEL.COMPANY} icon={<InfoIcon />} />
              <Tab
                value="containers"
                label={LABEL.CONTAINERS}
                icon={
                  <Badge
                    badgeContent={items.length}
                    showZero
                    color={items.length === 0 ? 'secondary' : 'primary'}
                  >
                    <ContainersIcon />
                  </Badge>
                }
              />
            </Tabs>
          </div>
        </div>
        <Divider />
        <div className={classes.divider} />
        {tabId === 'containers' && (
          <Paper>
            <ContainerTable
              columns={getColumns()}
              items={items}
              paging={paging}
              selected={items}
              loadSelectedSucceeded={loadSelectedContainersSucceeded}
              detailContainers
            />
          </Paper>
        )}
        {tabId === 'company' && (
          <main>
            {loading ? (
              <CircularProgress className={classes.companiesCircularProgress} />
            ) : (
              <div>
                <Paper className={classes.paper}>
                  <CompanyFilter />
                </Paper>
                <Paper className={classes.paper}>
                  <CompanyTable
                    columns={columnsCompanies}
                    items={companies}
                    paging={paging}
                    loadCompanies={loadCompanies}
                    loadCompaniesPage={loadCompaniesPage}
                    loadCompaniesRowsPerPage={loadCompaniesRowsPerPage}
                    headerTitle={LABEL.SELECT_COMPANY_TITLE}
                    onTransfer={(selectedStakeholder) => {
                      forwardContainers(items, selectedStakeholder);
                      setRefreshContainers(false);
                      items.length = 0;
                      navigate('/containers'); // Use navigate instead of history.push()
                    }}
                  />
                </Paper>
              </div>
            )}
            <main>
              <Button
                color="primary"
                style={{ marginRight: '15px' }}
                variant={'outlined'}
                onClick={() => navigate('/containers')} // Use navigate instead of history.push()
              >
                {LABEL.CANCEL.toUpperCase()}
              </Button>
            </main>
          </main>
        )}
      </main>
      <LoaderTimeout loading={loading} />
    </React.Fragment>
  );
};

ContainerDetail.propTypes = {
  classes: PropTypes.object,
  loadCompaniesFilters: PropTypes.func.isRequired,
  loadCompaniesPage: PropTypes.func.isRequired,
  loadCompaniesRowsPerPage: PropTypes.func.isRequired,
  errorMessage: PropTypes.func.isRequired,
  successMessage: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    items: getSelectedContainers(state),
    companies: getCompanies(state),
    paging: getCompaniesPaging(state),
    loading: getCompaniesLoading(state),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      setRefreshContainers,
      loadCompanies,
      loadCompaniesFilters,
      loadCompaniesPage,
      loadCompaniesRowsPerPage,
      loadSelectedContainersSucceeded,
      selectedStakeholderChanged,
      forwardContainers,
      errorMessage,
      successMessage,
    },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(ContainerDetail, styles));
